/**
 * IRL Generator panel — collects the deal setup + per-entity flags, posts
 * to /api/irl/generate, and shows the resulting IRL items in a checkbox
 * review table. Saving commits selected items to the existing irlStore so
 * the rest of the app sees a normal, uploaded-style IRL.
 *
 * The form mirrors the contract in `IRL_system_prompt (1).md`:
 *   Section 1 — Deal setup:
 *     - Historical period start (month picker → "MMM-YY")
 *     - Last completed fiscal year-end (month picker → "MMM-YY")
 *     - Fiscal years covered (free text — e.g., "FY24 and FY25")
 *     - Tax years referenced (dropdown — one/two/three/four/five, default three)
 *   Section 2 — Entities (repeating rows):
 *     - Name, Short alias, Audited, Operating, In perimeter
 *
 * Pre-flight validation (mirrors what the server would error on):
 *   - At least one entity must have Audited checked
 *   - Start ≤ end
 *   - Both months filled in
 */

import React, { useState } from "react";
import type { IRLRequest } from "../types";
import { saveIRLRequests } from "../store/irlStore";
import { apiFetch } from "../../api/fddClient";

type Props = {
  projectId: string;
  /** Called after the user saves so the parent can transition to the IRL review state. */
  onSaved: () => void;
  /** Called when the user backs out of the generator without saving. */
  onCancel: () => void;
};

type Sector = "midstream" | "renewables" | "fiber" | "more";

const SECTORS: { id: Sector; label: string; enabled: boolean }[] = [
  { id: "midstream", label: "Midstream", enabled: true },
  { id: "renewables", label: "Renewables", enabled: true },
  { id: "fiber", label: "Fiber", enabled: false },
  { id: "more", label: "More to come…", enabled: false },
];

const TAX_YEARS_OPTIONS = ["one", "two", "three", "four", "five"] as const;
type TaxYearsOption = (typeof TAX_YEARS_OPTIONS)[number];

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

type EntityRow = {
  id: number;
  name: string;
  shortAlias: string;
  isAudited: boolean;
  isOperating: boolean;
  isInPerimeter: boolean;
};

type GeneratedItem = {
  number: string;
  topic: string;
  entityName: string;
  request: string;
  status: "open";
  priority: "high" | "medium" | "low";
};

type ServerResponse = {
  sector: Sector;
  templateFile: string;
  items: GeneratedItem[];
  summary: {
    totalRows: number;
    auditedEntitiesUsed: string[];
    operatingEntitiesUsed: string[];
    operatingRowDeleted: boolean;
  };
  /** Populated xlsx (base64) — we trigger a download for the user as soon as we receive it. */
  populatedXlsxBase64: string;
};

let nextEntityId = 1;
const newEntityRow = (): EntityRow => ({
  id: nextEntityId++,
  name: "",
  shortAlias: "",
  isAudited: false,
  isOperating: false,
  isInPerimeter: true,
});

const IRLGeneratorPanel: React.FC<Props> = ({ projectId, onSaved, onCancel }) => {
  /* Deal setup */
  const [sector, setSector] = useState<Sector>("midstream");
  const [startMonth, setStartMonth] = useState(""); // "YYYY-MM" from <input type="month">
  const [endMonth, setEndMonth] = useState("");     // "YYYY-MM"
  const [historicalFiscalYears, setHistoricalFiscalYears] = useState("");
  const [taxYearsCount, setTaxYearsCount] = useState<TaxYearsOption>("three");

  /* Entities */
  const [entities, setEntities] = useState<EntityRow[]>(() => [newEntityRow()]);

  /* Generation flow */
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generated, setGenerated] = useState<ServerResponse | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  /* ─────── Entity table mutations ─────── */
  const addEntity = () => setEntities((rows) => [...rows, newEntityRow()]);
  const removeEntity = (id: number) =>
    setEntities((rows) => (rows.length > 1 ? rows.filter((r) => r.id !== id) : rows));
  const updateEntity = (id: number, patch: Partial<EntityRow>) =>
    setEntities((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  /* ─────── Pre-flight validation ─────── */
  const validate = (): string | null => {
    if (!startMonth) return "Pick a historical period start month.";
    if (!endMonth) return "Pick a last completed fiscal year-end month.";
    if (startMonth > endMonth) return "Historical period start must be on or before the last fiscal year-end.";
    if (!historicalFiscalYears.trim()) return "Enter the fiscal years covered (e.g., \"FY24 and FY25\").";
    const validEntities = entities.filter((e) => e.name.trim());
    if (validEntities.length === 0) return "Add at least one entity (with a name).";
    const audited = validEntities.filter((e) => e.isAudited);
    if (audited.length === 0) {
      return "At least one entity must be marked Audited (the IRL has audit-related rows that need an audited entity).";
    }
    const missingAlias = validEntities.find((e) => !e.shortAlias.trim());
    if (missingAlias) {
      return `Entity "${missingAlias.name}" is missing a short alias (e.g., "XYZ").`;
    }
    return null;
  };

  /* ─────── Submit ─────── */
  const handleGenerate = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    setGenerating(true);
    try {
      const cleanEntities = entities
        .filter((e) => e.name.trim())
        .map((e) => ({
          name: e.name.trim(),
          shortAlias: e.shortAlias.trim(),
          isAudited: e.isAudited,
          isOperating: e.isOperating,
          isInPerimeter: e.isInPerimeter,
        }));

      const res = await apiFetch("/api/irl/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sector,
          dealMetadata: {
            startingMonth: yyyyMmToMmmYy(startMonth),
            lastFyEndMonth: yyyyMmToMmmYy(endMonth),
            historicalFiscalYears: historicalFiscalYears.trim(),
            taxYearsCount,
          },
          entities: cleanEntities,
        }),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        setError(formatServerError(errBody));
        return;
      }

      const data = (await res.json()) as ServerResponse;
      // Kick off the xlsx download immediately so the user has the file
      // even if they navigate away from the review table.
      triggerXlsxDownload(data.populatedXlsxBase64, sector);
      setGenerated(data);
      setSelected(new Set(data.items.map((_, i) => i))); // all on by default
    } catch (e: any) {
      setError(e?.message || "Could not reach the IRL generator");
    } finally {
      setGenerating(false);
    }
  };

  /* ─────── Save selected rows to irlStore ─────── */
  const handleSave = () => {
    if (!generated) return;
    if (selected.size === 0) {
      setError("Select at least one question to save.");
      return;
    }
    const requests: IRLRequest[] = [];
    generated.items.forEach((item, idx) => {
      if (!selected.has(idx)) return;
      requests.push({
        id: `gen-${Date.now()}-${idx}`,
        number: item.number,
        topic: item.topic || undefined,
        entityName: item.entityName || undefined,
        request: item.request,
        status: item.status,
        priority: item.priority,
      });
    });
    saveIRLRequests(projectId, requests, `Generated (${generated.templateFile})`);
    onSaved();
  };

  const toggleRow = (idx: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });

  const toggleAll = (allOn: boolean) => {
    if (!generated) return;
    setSelected(allOn ? new Set(generated.items.map((_, i) => i)) : new Set());
  };

  /* ─────────────────────────── Render: review state ─────────────────────────── */
  if (generated) {
    const allOn = selected.size === generated.items.length;
    const someOn = selected.size > 0 && !allOn;
    const summary = generated.summary;
    return (
      <div style={containerStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontWeight: 700, color: "var(--theme-ink)", fontSize: 14 }}>
              Generated IRL — {summary.totalRows} rows
            </div>
            <div style={{ fontSize: 12, color: "var(--theme-ink-soft)" }}>
              {summary.auditedEntitiesUsed.length} audited entit{summary.auditedEntitiesUsed.length === 1 ? "y" : "ies"}
              {" · "}
              {summary.operatingEntitiesUsed.length} operating
              {summary.operatingRowDeleted ? " (operating-reports row dropped — no operating entities)" : ""}
            </div>
            <div style={{ fontSize: 11, color: "var(--theme-action)", marginTop: 4 }}>
              ✓ Populated <code style={{ background: "var(--theme-tint-21)", padding: "1px 4px", borderRadius: 3 }}>.xlsx</code> downloaded to your Downloads folder. Review below and save the items you want into the project's IRL.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onCancel} style={secondaryBtn}>Back</button>
            <button
              onClick={handleSave}
              disabled={selected.size === 0}
              style={{ ...primaryBtn, background: selected.size === 0 ? "var(--theme-action-disabled)" : "var(--theme-action)" }}
            >
              ✅ Save IRL ({selected.size})
            </button>
          </div>
        </div>

        {error && <div style={errorStyle}>{error}</div>}

        <div style={{ border: "1px solid var(--theme-border)", borderRadius: 8, overflow: "auto", maxHeight: 600 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--theme-tint-7)", borderBottom: "1px solid var(--theme-border)", position: "sticky", top: 0, zIndex: 1 }}>
                <th style={{ ...thStyle, width: 40 }}>
                  <input
                    type="checkbox"
                    checked={allOn}
                    ref={(el) => { if (el) el.indeterminate = someOn; }}
                    onChange={() => toggleAll(!allOn)}
                    style={{ cursor: "pointer" }}
                  />
                </th>
                <th style={{ ...thStyle, width: 60 }}>#</th>
                <th style={{ ...thStyle, width: 140 }}>Topic</th>
                <th style={{ ...thStyle, width: 160 }}>Entity</th>
                <th style={thStyle}>Request</th>
              </tr>
            </thead>
            <tbody>
              {generated.items.map((item, idx) => {
                const isOn = selected.has(idx);
                return (
                  <tr
                    key={idx}
                    onClick={() => toggleRow(idx)}
                    style={{
                      borderBottom: "1px solid var(--theme-tint-17)",
                      cursor: "pointer",
                      background: isOn ? "#fff" : "#f9f9f9",
                      opacity: isOn ? 1 : 0.6,
                    }}
                  >
                    <td style={{ padding: "8px 10px" }}>
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={() => toggleRow(idx)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ cursor: "pointer" }}
                      />
                    </td>
                    <td style={{ padding: "8px 10px", fontFamily: "monospace", color: "var(--theme-ink-soft)" }}>{item.number}</td>
                    <td style={{ padding: "8px 10px", color: "var(--theme-ink-soft)" }}>{item.topic}</td>
                    <td style={{ padding: "8px 10px", color: "var(--theme-ink-soft)" }}>{item.entityName}</td>
                    <td style={{ padding: "8px 10px", color: "#333" }}>{item.request}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /* ─────────────────────────── Render: form state ─────────────────────────── */
  return (
    <div style={containerStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 700, color: "var(--theme-ink)", fontSize: 16 }}>Generate IRL</div>
          <div style={{ fontSize: 12, color: "var(--theme-ink-soft)" }}>
            Pick a sector, fill in the deal setup, and list the entities in scope. We'll generate a starting IRL you can prune before saving.
          </div>
        </div>
        <button onClick={onCancel} style={secondaryBtn}>Cancel</button>
      </div>

      {/* ── Section 1: Deal setup ───────────────────────────── */}
      <SectionHeading>1. Deal setup</SectionHeading>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Field label="Sector template">
          <select
            value={sector}
            onChange={(e) => setSector(e.target.value as Sector)}
            style={inputStyle}
          >
            {SECTORS.map((s) => (
              <option key={s.id} value={s.id} disabled={!s.enabled}>
                {s.label}{!s.enabled ? " — coming soon" : ""}
              </option>
            ))}
          </select>
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Historical period — start month">
            <input
              type="month"
              value={startMonth}
              onChange={(e) => setStartMonth(e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="Last completed fiscal year-end">
            <input
              type="month"
              value={endMonth}
              onChange={(e) => setEndMonth(e.target.value)}
              style={inputStyle}
            />
          </Field>
        </div>

        <Field label="Fiscal years covered">
          <input
            type="text"
            value={historicalFiscalYears}
            onChange={(e) => setHistoricalFiscalYears(e.target.value)}
            placeholder='e.g., "FY24 and FY25"'
            style={inputStyle}
          />
        </Field>

        <Field label="Tax years referenced">
          <select
            value={taxYearsCount}
            onChange={(e) => setTaxYearsCount(e.target.value as TaxYearsOption)}
            style={inputStyle}
          >
            {TAX_YEARS_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </Field>
      </div>

      {/* ── Section 2: Entities ───────────────────────────── */}
      <SectionHeading>2. Entities</SectionHeading>
      <div style={{ fontSize: 11, color: "var(--theme-ink-soft)", marginBottom: 6 }}>
        Each entity gets a row. Mark which entities are <strong>Audited</strong>{" "}
        (have standalone audited financials), <strong>Operating</strong> (have monthly operating reports),
        and <strong>In perimeter</strong> (within the transaction perimeter — drives the "Company" definition in the IRL intro).
      </div>
      <div style={{ border: "1px solid var(--theme-border)", borderRadius: 8, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "var(--theme-tint-7)" }}>
              <th style={{ ...thStyle, width: "32%" }}>Name</th>
              <th style={{ ...thStyle, width: 110 }}>Short alias</th>
              <th style={{ ...thStyle, width: 80, textAlign: "center" }}>Audited</th>
              <th style={{ ...thStyle, width: 80, textAlign: "center" }}>Operating</th>
              <th style={{ ...thStyle, width: 80, textAlign: "center" }}>In perimeter</th>
              <th style={{ ...thStyle, width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {entities.map((e) => (
              <tr key={e.id} style={{ borderTop: "1px solid var(--theme-tint-17)" }}>
                <td style={{ padding: 4 }}>
                  <input
                    type="text"
                    value={e.name}
                    onChange={(ev) => updateEntity(e.id, { name: ev.target.value })}
                    placeholder="XYZ Pipeline LLC"
                    style={{ ...inputStyle, width: "100%", padding: "6px 8px" }}
                  />
                </td>
                <td style={{ padding: 4 }}>
                  <input
                    type="text"
                    value={e.shortAlias}
                    onChange={(ev) => updateEntity(e.id, { shortAlias: ev.target.value })}
                    placeholder="XYZ"
                    style={{ ...inputStyle, width: "100%", padding: "6px 8px" }}
                  />
                </td>
                <td style={{ padding: 4, textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={e.isAudited}
                    onChange={(ev) => updateEntity(e.id, { isAudited: ev.target.checked })}
                  />
                </td>
                <td style={{ padding: 4, textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={e.isOperating}
                    onChange={(ev) => updateEntity(e.id, { isOperating: ev.target.checked })}
                  />
                </td>
                <td style={{ padding: 4, textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={e.isInPerimeter}
                    onChange={(ev) => updateEntity(e.id, { isInPerimeter: ev.target.checked })}
                  />
                </td>
                <td style={{ padding: 4, textAlign: "center" }}>
                  <button
                    onClick={() => removeEntity(e.id)}
                    disabled={entities.length === 1}
                    title="Remove entity"
                    style={{
                      background: "transparent",
                      color: entities.length === 1 ? "#9ca3af" : "#dc2626",
                      border: "1px solid",
                      borderColor: entities.length === 1 ? "#e5e7eb" : "#fca5a5",
                      borderRadius: 4,
                      padding: "2px 8px",
                      fontSize: 11,
                      cursor: entities.length === 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        onClick={addEntity}
        style={{
          marginTop: 8,
          background: "#fff",
          color: "var(--theme-ink)",
          border: "1px solid var(--theme-border)",
          borderRadius: 6,
          padding: "6px 12px",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          alignSelf: "flex-start",
        }}
      >
        + Add entity
      </button>

      {error && <div style={{ ...errorStyle, marginTop: 12 }}>{error}</div>}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <button
          onClick={handleGenerate}
          disabled={generating}
          style={{ ...primaryBtn, background: generating ? "var(--theme-action-disabled)" : "var(--theme-action)" }}
        >
          {generating ? "Generating…" : "📋 Generate IRL"}
        </button>
      </div>
    </div>
  );
};

/* ───────────────────────────── Helpers ───────────────────────────── */

/** Decode the base64 xlsx and trigger a browser download with a sensible filename. */
function triggerXlsxDownload(base64: string, sector: Sector): void {
  if (!base64) return;
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `IRL - ${sector} - ${stamp}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Convert "2024-01" (HTML month input) to "Jan-24" (template format). */
function yyyyMmToMmmYy(s: string): string {
  if (!s) return "";
  const [yyyy, mm] = s.split("-");
  const monthIdx = Number(mm) - 1;
  if (isNaN(monthIdx) || monthIdx < 0 || monthIdx > 11) return "";
  return `${MONTH_NAMES[monthIdx]}-${yyyy.slice(2)}`;
}

function formatServerError(body: any): string {
  if (!body || typeof body !== "object") return "Generation failed.";
  switch (body.error) {
    case "missing_input":
      return `Missing required field${(body.fields || []).length > 1 ? "s" : ""}: ${(body.fields || []).join(", ")}`;
    case "no_audited_entities":
    case "invalid_period":
    case "template_missing":
      return body.message || body.error;
    case "bad_date_format":
      return `Bad date format on ${body.field} — expected ${body.expected}.`;
    case "internal":
      return body.message || "Internal error.";
    default:
      return body.message || body.error || "Generation failed.";
  }
}

/* ───────────────────────────── Style helpers ───────────────────────────── */

const containerStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid var(--theme-border)",
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
};

const inputStyle: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid var(--theme-border)",
  fontSize: 13,
  background: "#fff",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  color: "var(--theme-ink)",
  fontWeight: 600,
  fontSize: 12,
};

const primaryBtn: React.CSSProperties = {
  background: "var(--theme-action)",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "10px 16px",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 13,
};

const secondaryBtn: React.CSSProperties = {
  background: "#fff",
  color: "var(--theme-ink-soft)",
  border: "1px solid var(--theme-border)",
  borderRadius: 6,
  padding: "6px 12px",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 600,
};

const errorStyle: React.CSSProperties = {
  background: "#fef2f2",
  border: "1px solid #fca5a5",
  borderRadius: 8,
  padding: "8px 12px",
  fontSize: 12,
  color: "#991b1b",
  marginTop: 6,
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{label}</span>
    {children}
  </label>
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    fontWeight: 700,
    color: "var(--theme-ink)",
    fontSize: 13,
    marginTop: 16,
    marginBottom: 6,
    paddingBottom: 4,
    borderBottom: "1px solid var(--theme-border)",
  }}>{children}</div>
);

export default IRLGeneratorPanel;
